import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EnhancedServiceCard from '../components/EnhancedServiceCard';
import TestimonialSlider from '../components/TestimonialSlider';
import FAQ from '../components/FAQ';
import Stats from '../components/Stats';
import ScrollProgress from '../components/ScrollProgress';
import FloatingElements from '../components/FloatingElements';
import { Button } from '../components/ui/button';
import { illus, becomeWorkerIcon, needServiceIcon, getExclusiveOffers } from '../assets/assets';
import {
  ArrowRight,
  Search,
  Calendar,
  CheckCircle,
  Star,
  ShieldCheck,
  IndianRupee,
  Award,
  Headphones,
  Play,
  Zap,
  Users,
  Clock
} from 'lucide-react';
import { services, howItWorksSteps, whyChooseMaidly } from '../utils/mockData';

const iconMap = {
  Search,
  Calendar,
  CheckCircle,
  Star,
  ShieldCheck,
  IndianRupee,
  Award,
  Headphones
};

const Home = () => {
  const [email, setEmail] = useState('');

  const handleWaitlistSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    alert(`Thanks for joining! We'll contact you at ${email}`);
    setEmail('');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-green-50 to-blue-50">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-teal-200/30 to-transparent rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              rotate: [0, -90, 0],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-green-200/30 to-transparent rounded-full blur-3xl"
          />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-12 md:gap-0">
            {/* Left Content */}
            <div className="w-full md:w-1/2 text-center md:text-left">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="text-5xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                  Your Home,
                  <br />
                  <span className="bg-gradient-to-r from-teal-600 to-green-600 bg-clip-text text-transparent">
                    Our Care
                  </span>
                </h1>
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-lg md:text-xl text-gray-600 mb-12 max-w-3xl mx-auto md:mx-0 leading-relaxed"
              >
                Book trusted professionals for cleaning, plumbing, electrical work, and more. Quality service at your doorstep.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start items-center md:items-start"
              >
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-[#123D76] to-[#396BA9]  text-white px-8 py-6 rounded-full text-lg shadow-lg hover:shadow-xl transition-all hover:scale-105"
                  onClick={() => window.location.href = '/services'}
                >
                  Book a Service
                  <ArrowRight className="ml-2" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-gray-300 hover:border-teal-600 text-gray-700 hover:text-white px-8 py-6 rounded-full text-lg group"
                  onClick={() => window.location.href = '/how-it-works'}
                >
                  <Play className="mr-2 group-hover:scale-110 transition-transform" />
                  How It Works
                </Button>
              </motion.div>

              {/* Floating Trust Badges */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="mt-16 flex flex-wrap justify-center md:justify-start gap-8 text-sm text-gray-600"
              >
                <div className="flex items-center">
                  <ShieldCheck className="w-5 h-5 text-teal-600 mr-2" />
                  Verified Professionals
                </div>
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-400 mr-2" />
                  4.8/5 Rating
                </div>
                <div className="flex items-center">
                  <Award className="w-5 h-5 text-teal-600 mr-2" />
                  100% Satisfaction
                </div>
              </motion.div>
            </div>
            {/* Right Illustration */}
            <div className="w-full md:w-1/2 flex justify-center md:justify-end mb-10 md:mb-0">
              <img
                src={illus}
                alt="Maidly Hero Illustration"
                className="max-w-xs md:max-w-lg w-full h-auto drop-shadow-lg select-none pointer-events-none"
                draggable="false"
              />
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center pt-2"
          >
            <div className="w-1 h-3 bg-gray-400 rounded-full"></div>
          </motion.div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Trusted by Thousands
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Join our growing community of satisfied customers
            </p>
          </motion.div>
          <Stats />
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Professional home services tailored to your needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <EnhancedServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              How Maidly Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Simple, quick, and hassle-free
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {howItWorksSteps.map((step, index) => {
              const Icon = iconMap[step.icon];
              return (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative text-center"
                >
                  {/* Step Number */}
                  <div className="relative inline-block mb-6  z-10">
                    <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-green-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <Icon className="w-10 h-10 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-white border-2 border-teal-500 rounded-full flex items-center justify-center text-teal-600 font-bold text-sm">
                      {step.step}
                    </div>
                  </div>

                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>

                  {/* Connector Line (except last item) */}
                  {index < howItWorksSteps.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-[60%] w-full h-0.5 bg-gradient-to-r from-teal-300 to-transparent "></div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Maidly */}
      <section className="py-20 bg-gradient-to-br from-teal-50 to-green-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Choose Maidly?
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Your trust is our top priority
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {whyChooseMaidly.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all hover:-translate-y-2"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-100 to-green-100 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-teal-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Sections */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {/* Customer CTA - left card */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl md:rounded-3xl bg-gradient-to-tr from-[#21D192] via-[#33BDE5] to-[#53E48F] shadow-xl flex flex-col md:flex-row items-center p-0 relative overflow-hidden"
              style={{ minHeight: "200px" }}
            >
              <div className="flex-1 flex flex-col justify-center items-start px-7 py-9 md:pl-10 md:pr-4 relative z-10">
                <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white">Need a Services?</h3>
                <p className="text-white/90 mb-5 text-base md:text-lg font-medium">
                  Book verified professionals for your home in minutes
                </p>
                <Button
                  size="lg"
                  className="bg-white text-[#19A777] font-semibold hover:bg-gray-50 rounded-full px-6 py-2 shadow transition"
                  onClick={() => window.location.href = '/services'}
                >
                  Browse Services
                  <ArrowRight className="ml-2" />
                </Button>
              </div>
              {/* Illustration: always show below content on mobile, on right for md+ */}
              <div className="flex w-full md:w-auto justify-center md:justify-end items-end md:items-end pb-4 md:pb-0 pr-0 md:pr-6">
                <img
                  src={needServiceIcon}
                  alt="Need Services illustration"
                  className=" w-auto object-contain drop-shadow-lg select-none pointer-events-none"
                  draggable="false"
                  style={{}}
                />
              </div>
            </motion.div>

            {/* Worker CTA - right card */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="rounded-2xl md:rounded-3xl bg-gradient-to-tr from-[#175EE2] via-[#3969F2] to-[#552CEE] shadow-xl flex flex-col md:flex-row items-center p-0 relative overflow-hidden"
              style={{ minHeight: "200px" }}
            >
              <div className="flex-1 flex flex-col justify-center items-start px-7 py-9 md:pl-10 md:pr-4 relative z-10">
                <h3 className="text-2xl md:text-3xl font-bold mb-2 text-white">Become a Worker?</h3>
                <p className="text-white/90 mb-5 text-base md:text-lg font-medium">
                  Become a verified professional and start earning money
                </p>
                <Button
                  size="lg"
                  className="bg-white text-[#175EE2] font-semibold hover:bg-gray-50 rounded-full px-6 py-2 shadow transition"
                  onClick={() => window.location.href = '/services'}
                >
                  Become a Worker
                  <ArrowRight className="ml-2" />
                </Button>
              </div>
              {/* Illustration: always show below content on mobile, on right for md+ */}
              <div className="flex w-full md:w-auto justify-center md:justify-end items-end md:items-end pb-4 md:pb-0 pr-0 md:pr-2">
                <img
                  src={becomeWorkerIcon}
                  alt="Become Worker illustration"
                  className="w-auto object-contain drop-shadow-lg select-none pointer-events-none"
                  draggable="false"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Real stories from real people
            </p>
          </motion.div>
          <TestimonialSlider />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to know
            </p>
          </motion.div>
          <FAQ />
        </div>
      </section>

      {/* Waitlist/Newsletter Section */}
      <section className="py-5 bg-gradient-to-br from-teal-50 via-green-50 to-blue-50">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row items-center justify-between max-w-5xl mx-auto gap-10 md:gap-16"
          >
            {/* Left: Text + form */}
            <div>
              <div className="flex-1 w-full md:text-left text-center ">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Get Exclusive Offers
                </h2>
                <p className="text-lg text-gray-600 mb-8">
                  Subscribe to receive special discounts and updates
                </p>
                <form
                  onSubmit={handleWaitlistSubmit}
                  className="flex flex-col sm:flex-row items-center gap-4 max-w-md md:max-w-none mt-0 mx-auto md:mx-0"
                >
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="flex-1 px-6 py-4 rounded-full border border-gray-300 focus:outline-none focus:border-teal-500 text-gray-900 transition-all"
                  />
                  <Button
                    type="submit"
                    size="lg"
                    className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-full whitespace-nowrap"
                  >
                    Subscribe
                  </Button>
                </form>
              </div>
            </div>
            {/* Right: SVG Image */}
            <div className="flex-1 flex justify-center md:justify-end w-full">
              <img
                src={getExclusiveOffers}
                alt="Get Exclusive Offers"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
